import { Card, Stack } from '@mui/material';
import {
    CreateButton,
    ListToolbar,
    Pagination,
    Title,
    TopToolbar,
} from 'react-admin';
import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ExpensesClaimSummary } from '@wwf/sdk';
import { ExpensesClaimListContent } from './ExpensesClaimListContent';
import { useExpensesClaimDrawer } from '../drawer/ExpensesClaimDrawer';

export const ExpensesClaimListLayout = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { openDrawer } = useExpensesClaimDrawer();

    const editIdFromUrl = searchParams.get('edit') as
        | ExpensesClaimSummary['id']
        | null;
    const lastHandledEditId = useRef<ExpensesClaimSummary['id'] | null>(null);

    if (!editIdFromUrl) {
        lastHandledEditId.current = null;
    }

    useEffect(() => {
        if (!editIdFromUrl || editIdFromUrl === lastHandledEditId.current) {
            return;
        }
        const id = editIdFromUrl as ExpensesClaimSummary['id'];
        if (Number.isNaN(id)) {
            return;
        }
        lastHandledEditId.current = editIdFromUrl;
        openDrawer('edit', id as ExpensesClaimSummary['id']);
        setSearchParams(
            current => {
                const next = new URLSearchParams(current);
                next.delete('edit');
                return next;
            },
            { replace: true }
        );
    }, [editIdFromUrl, openDrawer, setSearchParams]);

    return (
        <Stack direction="row">
            <Stack sx={{ width: '100%' }}>
                <Title title={'Expenses Claims'} />
                <ListToolbar
                    actions={
                        <ExpensesClaimListActions
                            openCreateDrawer={() => openDrawer('create', null)}
                        />
                    }
                />
                <Card>
                    <ExpensesClaimListContent
                        openEditDrawer={(id: ExpensesClaimSummary['id']) =>
                            openDrawer('show', id)
                        }
                    />
                </Card>
                <Pagination rowsPerPageOptions={[10, 25, 50, 100]} />
            </Stack>
        </Stack>
    );
};

const ExpensesClaimListActions = ({
    openCreateDrawer,
}: {
    openCreateDrawer: () => void;
}) => (
    <TopToolbar>
        <CreateButton
            variant="contained"
            label="New Expenses Claim"
            resource="expensesClaim"
            onClick={openCreateDrawer}
            sx={{ marginLeft: 2 }}
        />
    </TopToolbar>
);
